// The Manuals library's "Vocabulary and troubleshooting" card (Session BBX3). Two things are read out of the manuals' own
// stored text, never downloaded again and never generated:
//   - the search vocabulary, which lets a misspelt word or maker name in a question be corrected to a real one (the
//     correction is always shown to the person) and powers the suggestions as you type;
//   - the printed fault code table rows and troubleshooting rows (a symptom, its possible causes in the order printed, and
//     the remedy), shown exactly as printed.
// Both are previewed first and saved through a two-step confirm. Both can be removed, and read again.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Loader } from "../../components/Loader";
import type {
  KnowledgeFaultRowRecord,
  KnowledgeFaultRowSample,
  KnowledgeFaultRowsBackfill,
  KnowledgeVocabularyStatus,
} from "../../lib/knowledgeTypes";

const QUIET_BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1.5 text-sm text-ele-strong hover:bg-ele-surface-raised disabled:opacity-50";

const SOURCE_WORDS: Record<string, string> = { text: "the page's own text", ocr: "a scan read by OCR", vision: "a page read by the vision pass" };

function SampleList({ samples }: { samples: KnowledgeFaultRowSample[] }) {
  if (samples.length === 0) return null;
  return (
    <ul className="mt-1 space-y-2">
      {samples.map((s, i) => (
        <li key={i} className="rounded-ele border border-ele-purple-accent p-2 text-sm">
          <p className="text-ele-strong">
            <strong>{s.manual}</strong> <span className="text-ele-body">page {s.page}</span>
            {s.safety && <span className="text-ele-body"> (safety step)</span>}
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words font-mono text-[13px] text-ele-body">{s.printed.join("\n")}</p>
        </li>
      ))}
    </ul>
  );
}

export function KnowledgeVocabularyCard() {
  const [status, setStatus] = useState<KnowledgeVocabularyStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backfill, setBackfill] = useState<KnowledgeFaultRowsBackfill | null>(null);
  const [rebuildPreview, setRebuildPreview] = useState<{ message: string; documents: number } | null>(null);
  const [kind, setKind] = useState<"code" | "symptom">("code");
  const [rows, setRows] = useState<{ total: number; rows: KnowledgeFaultRowRecord[] } | null>(null);
  const [toDelete, setToDelete] = useState<{ kind: "row"; row: KnowledgeFaultRowRecord } | { kind: "all-rows" } | { kind: "vocabulary" } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api.knowledge.drive.vocabularyStatus());
      setError(null);
    } catch (err) {
      setError(friendlyApiError(err, "The vocabulary could not be loaded."));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadRows(k: "code" | "symptom") {
    try {
      setKind(k);
      setRows(await api.knowledge.drive.faultRows({ kind: k, limit: 20 }));
    } catch (err) {
      setError(friendlyApiError(err, "The rows could not be loaded."));
    }
  }

  async function previewRebuild() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.knowledge.drive.vocabularyRebuild({ preview: true });
      setRebuildPreview({ message: r.message, documents: r.documents ?? 0 });
    } catch (err) {
      setError(friendlyApiError(err, "The vocabulary could not be checked."));
    } finally {
      setBusy(false);
    }
  }

  async function runRebuild() {
    if (!rebuildPreview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.knowledge.drive.vocabularyRebuild({ preview: false, confirmDocuments: rebuildPreview.documents });
      setMessage(r.message);
      setRebuildPreview(null);
      await load();
    } catch (err) {
      setError(friendlyApiError(err, "The vocabulary could not be rebuilt."));
    } finally {
      setBusy(false);
    }
  }

  async function previewBackfill(refresh = false) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.knowledge.drive.faultRowsBackfill({ preview: true, ...(refresh ? { refresh: true } : {}) });
      setBackfill(r);
      if (r.running) setMessage(r.message);
    } catch (err) {
      setError(friendlyApiError(err, "The pages could not be read."));
    } finally {
      setBusy(false);
    }
  }

  async function runBackfill() {
    if (!backfill || backfill.totalRows === undefined) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.knowledge.drive.faultRowsBackfill({ preview: false, confirmRows: backfill.totalRows });
      setMessage(r.message);
      if (!r.running) setBackfill(null);
      await load();
      if (!r.running) await loadRows(kind);
    } catch (err) {
      setError(friendlyApiError(err, "The rows could not be saved."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    setBusy(true);
    setDeleteError(null);
    try {
      const r =
        toDelete.kind === "row"
          ? await api.knowledge.drive.faultRowDelete(toDelete.row.id, false)
          : toDelete.kind === "all-rows"
            ? await api.knowledge.drive.faultRowsDelete({ all: true }, false)
            : await api.knowledge.drive.vocabularyClear(false);
      setMessage(r.message);
      setToDelete(null);
      await load();
      if (rows) await loadRows(kind);
    } catch (err) {
      setDeleteError(friendlyApiError(err, "That could not be removed."));
    } finally {
      setBusy(false);
    }
  }

  const deleteText = (() => {
    if (!toDelete) return { title: "", message: "" };
    if (toDelete.kind === "row") {
      const what = toDelete.row.kind === "code" ? `code ${toDelete.row.code}` : `the symptom "${toDelete.row.symptom}"`;
      return { title: "Remove this printed row?", message: `The row for ${what} on page ${toDelete.row.pageNumber} of ${toDelete.row.fileName} would be removed. Nothing depends on it, and reading the library again brings it back.` };
    }
    if (toDelete.kind === "all-rows") {
      return { title: "Remove every printed row?", message: "Every fault code and troubleshooting row would be removed. Searches go back to reading the page text. Reading the library again brings them back." };
    }
    return { title: "Remove the vocabulary?", message: "Spelling correction and suggestions as you type stop until the vocabulary is rebuilt. Nothing else changes." };
  })();

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-labelledby="kb-vocab-heading">
      <h2 id="kb-vocab-heading" className="font-heading text-lg text-ele-blue">
        Vocabulary and troubleshooting
      </h2>
      <p className="mt-1 text-sm text-ele-body">
        The words the manuals use let a misspelt word in a question be corrected (the correction is always shown). The manuals&apos; own fault code tables and
        troubleshooting tables are read once, kept exactly as printed, and shown with their manual and page. Nothing is downloaded again, no vision is spent and
        nothing is written by a model.
      </p>
      {message && (
        <p role="status" className="mt-2 text-sm text-ele-strong">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      {status === null ? (
        error ? null : <Loader label="Loading the vocabulary" />
      ) : (
        <>
          <ul className="mt-3 grid gap-1 text-sm text-ele-strong sm:grid-cols-2">
            <li>
              Vocabulary terms: <strong>{status.vocabulary.total.toLocaleString("en-GB")}</strong>
            </li>
            <li>
              Last rebuilt:{" "}
              <strong>{status.vocabulary.lastRebuiltAt ? new Date(status.vocabulary.lastRebuiltAt).toLocaleString("en-GB") : "not yet"}</strong>
            </li>
            <li>
              Fault code rows: <strong>{status.faultRows.codeRows.toLocaleString("en-GB")}</strong>
            </li>
            <li>
              Troubleshooting rows: <strong>{status.faultRows.symptomRows.toLocaleString("en-GB")}</strong>
            </li>
          </ul>

          {status.calibration && (
            <p className="mt-2 text-sm text-ele-body" data-testid="calibration-status">
              Likely floor: <strong>{status.calibration.calibrated ? "calibrated" : "not calibrated"}</strong>. {status.calibration.message}
            </p>
          )}

          {status.faultRows.byMaker.length > 0 && (
            <div className="mt-3 overflow-hidden">
              <table className="ele-list-table w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left">Maker</th>
                    <th className="text-right">Codes</th>
                    <th className="text-right">Symptom rows</th>
                    <th className="text-right">Skipped pages</th>
                  </tr>
                </thead>
                <tbody>
                  {status.faultRows.byMaker.slice(0, 40).map((m) => (
                    <tr key={m.makerKey}>
                      <td className="truncate">{m.display}</td>
                      <td className="text-right">{m.codeRows.toLocaleString("en-GB")}</td>
                      <td className="text-right">{m.symptomRows.toLocaleString("en-GB")}</td>
                      <td className="text-right">{m.skippedPages.toLocaleString("en-GB")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {status.lastBackfill && status.lastBackfill.skippedPages.length > 0 && (
            <p className="mt-2 text-sm text-ele-body">
              Pages skipped at the last read:{" "}
              {status.lastBackfill.skippedPages.map((s) => `${s.pages.toLocaleString("en-GB")} (${s.label.toLowerCase()})`).join("; ")}.
            </p>
          )}

          {backfill && !backfill.running && backfill.rowsBeforeFilter !== undefined && (
            <div className="mt-3 text-sm text-ele-body" data-testid="backfill-filter-summary">
              <p>
                Rows read: <strong>{backfill.rowsBeforeFilter.toLocaleString("en-GB")}</strong>. Rejected as not printed fault rows:{" "}
                <strong>{(backfill.rejectedRows ?? 0).toLocaleString("en-GB")}</strong>. Kept: <strong>{(backfill.totalRows ?? 0).toLocaleString("en-GB")}</strong>.
              </p>
              {backfill.rejectedByRule && backfill.rejectedByRule.some((r) => r.rows > 0) && (
                <ul className="mt-1 list-disc pl-5">
                  {backfill.rejectedByRule.filter((r) => r.rows > 0).map((r) => (
                    <li key={r.rule}>
                      {r.label}: {r.rows.toLocaleString("en-GB")}
                    </li>
                  ))}
                </ul>
              )}
              {backfill.byMaker && backfill.byMaker.some((m) => (m.rejectedRows ?? 0) > 0) && (
                <table className="ele-list-table mt-2 w-full text-sm" data-testid="backfill-maker-table">
                  <thead>
                    <tr>
                      <th className="text-left">Maker</th>
                      <th className="text-right">Read</th>
                      <th className="text-right">Rejected</th>
                      <th className="text-right">Kept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backfill.byMaker.slice(0, 40).map((m) => (
                      <tr key={m.makerKey}>
                        <td className="truncate">{m.display}</td>
                        <td className="text-right">{(m.rowsBeforeFilter ?? 0).toLocaleString("en-GB")}</td>
                        <td className="text-right">{(m.rejectedRows ?? 0).toLocaleString("en-GB")}</td>
                        <td className="text-right">{(m.codeRows + m.symptomRows).toLocaleString("en-GB")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {backfill.excludedDocuments && backfill.excludedDocuments.length > 0 && (
                <p className="mt-1">
                  Left out as not manuals: {backfill.excludedDocuments.map((e) => `${e.documents.toLocaleString("en-GB")} (${e.label.toLowerCase()})`).join("; ")}.
                </p>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!backfill || backfill.running ? (
              <Button type="button" onClick={() => void previewBackfill(false)} loading={busy} disabled={busy}>
                Read the printed rows (preview)
              </Button>
            ) : (
              <ConfirmButton
                label={`Save ${backfill.totalRows?.toLocaleString("en-GB") ?? ""} printed rows`}
                confirmLabel="Save them now?"
                onConfirm={runBackfill}
                disabled={busy}
              />
            )}
            {backfill && !backfill.running && (
              <button type="button" className={QUIET_BTN} onClick={() => setBackfill(null)} disabled={busy}>
                Cancel
              </button>
            )}
            {!rebuildPreview ? (
              <button type="button" className={QUIET_BTN} onClick={() => void previewRebuild()} disabled={busy}>
                Rebuild the vocabulary
              </button>
            ) : (
              <>
                <ConfirmButton label="Rebuild the vocabulary now" confirmLabel="Rebuild it now?" onConfirm={runRebuild} disabled={busy} />
                <button type="button" className={QUIET_BTN} onClick={() => setRebuildPreview(null)} disabled={busy}>
                  Cancel
                </button>
              </>
            )}
          </div>
          {rebuildPreview && (
            <p role="status" className="mt-2 text-sm text-ele-strong">
              {rebuildPreview.message}
            </p>
          )}
          {backfill && (
            <div className="mt-3" role="status">
              <p className="text-sm text-ele-strong">{backfill.message}</p>
              {!backfill.running && backfill.samples && (
                <div className="mt-2 space-y-3">
                  {(["code", "symptom"] as const).map((k) =>
                    (["text", "ocr", "vision"] as const).map((src) => {
                      const list = backfill.samples?.[k]?.[src] ?? [];
                      if (list.length === 0) return null;
                      return (
                        <div key={`${k}-${src}`}>
                          <h3 className="font-heading text-base text-ele-strong">
                            {k === "code" ? "Fault code rows" : "Troubleshooting rows"}, read from {SOURCE_WORDS[src]} ({list.length} shown)
                          </h3>
                          <SampleList samples={list} />
                        </div>
                      );
                    }),
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <h3 className="font-heading text-base text-ele-strong">Look at the saved rows</h3>
            <div className="mt-1 flex flex-wrap gap-2">
              <button type="button" className={QUIET_BTN} onClick={() => void loadRows("code")}>
                Fault codes
              </button>
              <button type="button" className={QUIET_BTN} onClick={() => void loadRows("symptom")}>
                Troubleshooting
              </button>
            </div>
            {rows && (
              <>
                <p className="mt-2 text-sm text-ele-body">
                  {rows.total.toLocaleString("en-GB")} {kind === "code" ? "fault code" : "troubleshooting"} rows; the first {rows.rows.length} are shown.
                </p>
                <ul className="mt-2 space-y-2">
                  {rows.rows.map((r) => (
                    <li key={r.id} className="rounded-ele border border-ele-purple-accent p-2 text-sm">
                      <p className="text-ele-strong">
                        <strong>{r.fileName}</strong> <span className="text-ele-body">page {r.pageNumber}</span>
                        {r.safety && <span className="text-ele-body"> (safety step)</span>}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap break-words font-mono text-[13px] text-ele-body">{r.printed.join("\n")}</p>
                      <button type="button" className={`${QUIET_BTN} mt-2`} onClick={() => setToDelete({ kind: "row", row: r })}>
                        Remove this row
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={QUIET_BTN} onClick={() => setToDelete({ kind: "all-rows" })} disabled={status.faultRows.total === 0}>
              Remove every printed row
            </button>
            <button type="button" className={QUIET_BTN} onClick={() => setToDelete({ kind: "vocabulary" })} disabled={status.vocabulary.total === 0}>
              Remove the vocabulary
            </button>
          </div>
        </>
      )}

      <ConfirmDeleteModal
        open={toDelete !== null}
        title={deleteText.title}
        message={deleteText.message}
        confirmLabel="Remove"
        busy={busy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          setToDelete(null);
          setDeleteError(null);
        }}
      />
    </section>
  );
}
